import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import * as React from "react";
import { FC, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { useParams } from "react-router";
import ReactTable, { RowInfo } from "react-table";
import * as ReactTooltip from "react-tooltip";
import { bindActionCreators } from "redux";
import { InlineLoading, Modal } from "src/components/common";
import { Link } from "src/components/routing";
import { FontAwesomeIcons } from "src/constants/fontAwesomeIcons";
import { PAGE_SIZE_OPTIONS } from "src/constants/reactTableConstants";
import { ModalSize } from "src/enumerations/ModalSize";
import { LaboratoryInstitutionsBreadcrumbs } from "src/helpers/breadcrumbHelper";
import {
    convertToApproximateDateString,
    convertToShortDateString,
} from "src/helpers/date";
import useNavigate from "src/hooks/useNavigate";
import * as InstitutionLaboratoryActions from "../../../actions/institutionLaboratory";
import * as LaboratoryActions from "../../../actions/laboratories";
import * as NavigationActions from "../../../actions/navigation";
import * as PagingActions from "../../../actions/pages/pagingPage";
import { RestrictedLayout } from "../../../components/layouts/index";
import * as Dtos from "../../../dtos/Tmd.dtos";
import { RequestState } from "../../../enumerations/RequestState";
import * as AuthenticationHelper from "../../../helpers/authentication";
import { LaboratorySubMenu } from "../../../helpers/subMenuHelper";
import { ITmdState } from "../../../reducers/index";

import "./LabInstitutionsPage.scss";

interface LabInstitutionsPageProps {
    laboratory: Dtos.Laboratory;
    loadingLaboratory: boolean;
    loadLaboratorySuccess: boolean;
    loadLaboratoryFailure: boolean;
    loadLaboratoryErrorCode: string;
    institutionLaboratories: Dtos.InstitutionLaboratory[];
    loadingInstitutionLaboratories: boolean;
    loadInstitutionLaboratoriesSuccess: boolean;
    loadInstitutionLaboratoriesFailure: boolean;
    loadInstitutionLaboratoriesErrorCode: string;
    permissions?: Dtos.PersonnelTmdPermissions[];
    pageSize: number;
    deletingInstitutionLaboratory: boolean;
    deleteInstitutionLaboratorySuccess: boolean;
    clearLaboratory: LaboratoryActions.ILaboratoryClearActionCreator;
    loadLaboratory: LaboratoryActions.ILaboratoryLoadByIdActionCreator;
    clearInstitutionLaboratories: InstitutionLaboratoryActions.IInstitutionLaboratoryClearActionCreator;
    loadInstitutionLaboratories: InstitutionLaboratoryActions.IInstitutionLaboratoryLoadByLaboratoryIdActionCreator;
    deleteInstitutionLaboratory: InstitutionLaboratoryActions.IInstitutionLaboratoryDeleteActionCreator;
    setBreadcrumbs: NavigationActions.ISetBreadcrumbsActionCreator;
    setDrawerItems: NavigationActions.ISetDrawerItemsActionCreator;
    setPageSize: PagingActions.IPagingPageSizeChangedStateActionCreator;
}

const LabInstitutionsPage: FC<LabInstitutionsPageProps> = ({
    laboratory,
    loadingLaboratory,
    loadLaboratorySuccess,
    loadLaboratoryFailure,
    loadLaboratoryErrorCode,
    institutionLaboratories,
    loadingInstitutionLaboratories,
    loadInstitutionLaboratoriesSuccess,
    loadInstitutionLaboratoriesFailure,
    loadInstitutionLaboratoriesErrorCode,
    permissions,
    pageSize,
    deletingInstitutionLaboratory,
    deleteInstitutionLaboratorySuccess,
    clearLaboratory,
    loadLaboratory,
    clearInstitutionLaboratories,
    loadInstitutionLaboratories,
    deleteInstitutionLaboratory,
    setBreadcrumbs,
    setDrawerItems,
    setPageSize
}) => {
    //
    // -- Params --
    const { laboratoryId } = useParams<{ laboratoryId: string }>();
    const laboratoryIdNumber = parseInt(laboratoryId, 10);

    //
    // -- Hooks --
    const navigate = useNavigate();

    //
    // -- States --
    const [showInActive, setShowInActive] = useState<boolean>(false);
    const [openModal, setOpenModal] = useState<boolean>(false);
    const [labInstitution, setLabInstitution] = useState<
        Dtos.InstitutionLaboratory | undefined
    >(undefined);

    //
    // -- Variables --
    const showInstitutions = 
    !loadingLaboratory && 
    loadLaboratorySuccess && 
    !loadingInstitutionLaboratories && 
    loadInstitutionLaboratoriesSuccess;



    // Load Laboratory & institutions
    useEffect(() => {
        if (laboratoryIdNumber && !laboratory) {
            setDrawerItems(LaboratorySubMenu(laboratoryIdNumber, permissions));
            setBreadcrumbs(
                LaboratoryInstitutionsBreadcrumbs(laboratoryIdNumber)
            );

            loadLaboratory(laboratoryIdNumber);
            loadInstitutionLaboratories(laboratoryIdNumber);
        }
    }, [
        laboratoryIdNumber,
        laboratory,
        permissions,
        setDrawerItems,
        setBreadcrumbs,
        loadLaboratory,
        loadInstitutionLaboratories,
    ]);

    // Navigate to error page on load failure
    useEffect(() => {
        if (loadLaboratoryFailure) {
            navigate(`/error/${loadLaboratoryErrorCode}`);
        }
        if (loadInstitutionLaboratoriesFailure) {
            navigate(`/error/${loadInstitutionLaboratoriesErrorCode}`);
        }
    }, [
        loadLaboratoryFailure, 
        loadLaboratoryErrorCode, 
        loadInstitutionLaboratoriesFailure, 
        loadInstitutionLaboratoriesErrorCode, 
        navigate
    ]);

    // Update breadcrumbs
    useEffect(() => {
        if (laboratoryIdNumber && laboratory?.name) {
            setBreadcrumbs(
                LaboratoryInstitutionsBreadcrumbs(
                    laboratoryIdNumber,
                    laboratory.name
                )
            );
        }
    }, [
        laboratory,
        laboratoryIdNumber,
        permissions,
        setDrawerItems,
        setBreadcrumbs,
    ]);

    // Cleanup effect to clear the laboratory & institutions on component unmount
    useEffect(() => {
        return () => {
            clearLaboratory();
            clearInstitutionLaboratories();
        };
    }, [clearLaboratory, clearInstitutionLaboratories]);

    // Reload laboratory after delete
    useEffect(() => {
        if (
            !deletingInstitutionLaboratory &&
            deleteInstitutionLaboratorySuccess
        ) {
            loadLaboratory(laboratoryIdNumber);
            loadInstitutionLaboratories(laboratoryIdNumber);
        }
    }, [
        deletingInstitutionLaboratory,
        deleteInstitutionLaboratorySuccess,
        loadLaboratory,
        loadInstitutionLaboratories,
        laboratoryIdNumber,
    ]);

    const generateDeleteButton = (
        institutionLab: Dtos.InstitutionLaboratory
    ) => {
        const hasPermission = AuthenticationHelper.hasPermission(
            permissions,
            Dtos.Permission.LabInstitutionUpdate,
            undefined,
            undefined
        );

        if (!hasPermission) return null;

        return (
            <button
                key={`labDeleteButton-${institutionLab.id}`}
                className="btn btn-outline-secondary btn-sm btn-row-version"
                onClick={(event) => {
                    event.stopPropagation();
                    event.preventDefault();
                    setLabInstitution(institutionLab);
                    setOpenModal(true);
                }}
            >
                <FontAwesomeIcon
                    icon={FontAwesomeIcons.Duotone.TRASH}
                    fixedWidth
                />
            </button>
        );
    };

    const createTable = useMemo(() => {
        if (
            !showInActive &&
            institutionLaboratories?.filter(
                (x) => x?.active == true
            )?.length === 0 &&
            institutionLaboratories?.filter(
                (x) => x?.active == false
            )?.length > 0
        ) {
            return (
                <div className="card card-shadow p-3">
                    <div className="alert alert-info d-flex mb-0">
                        <div className="mr-2">
                            <FontAwesomeIcon
                                icon={FontAwesomeIcons.Solid.INFO_SQUARE}
                                fixedWidth
                            />
                        </div>
                        <span>
                            There are currently no active institutions to view.
                        </span>
                    </div>
                </div>
            );
        }

        let tableData = showInActive
            ? institutionLaboratories
            : institutionLaboratories?.filter(
                    (x) => x?.active == true
                );

        if (!tableData || tableData?.length == 0) {
            return (
                <div className="card card-shadow p-3">
                    <div className="alert alert-info d-flex mb-0">
                        <div className="mr-2">
                            <FontAwesomeIcon
                                icon={FontAwesomeIcons.Solid.INFO_SQUARE}
                                fixedWidth
                            />
                        </div>
                        <span>
                            There are currently no institutions to view.
                        </span>
                    </div>
                </div>
            );
        }

        const showPagination = tableData.length > pageSize;

        return (
            <div className="table-wrapper">
                <ReactTable
                    data={tableData}
                    pageSizeOptions={PAGE_SIZE_OPTIONS}
                    defaultPageSize={
                        showPagination ? pageSize : tableData.length
                    }
                    pageSize={showPagination ? pageSize : tableData.length}
                    showPagination={showPagination}
                    className="-striped -highlight -clickable"
                    noDataText="There are currently no institutions to display"
                    resizable={false}
                    onPageSizeChange={(val) => setPageSize(val)}
                    getTdProps={(
                        _state,
                        rowInfo: RowInfo,
                        _column,
                        _instance
                    ) => {
                        return {
                            onClick: (_e, handleOriginal) => {
                                const url: string = `/labs/${laboratoryIdNumber}/institutions/${rowInfo.original.id}/details`;
                                navigate(url);

                                if (handleOriginal) {
                                    handleOriginal();
                                }
                            },
                        };
                    }}
                    defaultSorted={[
                        {
                            id: "name",
                            desc: false,
                        },
                    ]}
                    columns={[
                        {
                            id: "id",
                            Header: " ",
                            accessor: "id",
                            maxWidth: 50,
                            Cell: (_props) => (
                                <FontAwesomeIcon
                                    size="lg"
                                    icon={FontAwesomeIcons.Duotone.INSTITUTION}
                                    fixedWidth
                                />
                            ),
                        },
                        {
                            id: "institution.name",
                            Header: "Institution",
                            accessor: "institution.name",
                        },
                        {
                            id: "dateCeased",
                            Header: "Date Ceased",
                            accessor: "dateCeased",
                            Cell: (props) => (
                                <div>
                                    <span
                                        data-tip={convertToApproximateDateString(
                                            props.value
                                        )}
                                    >
                                        {convertToShortDateString(props.value)}
                                    </span>
                                    <ReactTooltip />
                                </div>
                            ),
                        },
                        {
                            id: "deleteDepartment",
                            Header: " ",
                            maxWidth: 75,
                            Cell: (props) => (
                                <div>
                                    {generateDeleteButton(props.original)}
                                </div>
                            ),
                        },
                    ]}
                />
            </div>
        );
    }, [
        institutionLaboratories,
        pageSize,
        PAGE_SIZE_OPTIONS,
        navigate,
        setPageSize,
        showInActive,
    ]);

    const renderContent = useMemo(() => {
        return (
            <div>
                <div className="row">
                    <div className={"col-6 mb-2"}>
                        <h1>Institutions</h1>
                    </div>
                    <div className="col-6" style={{ textAlign: "right" }}>
                        <button
                            onClick={() => {
                                setShowInActive(!showInActive);
                            }}
                            type="button"
                            className="btn btn-outline-dark mr-2"
                            style={{ minWidth: "125px" }}
                        >
                            {showInActive ? "Hide Inactive" : "Show Inactive"}
                        </button>
                        {AuthenticationHelper.hasPermission(
                            permissions,
                            Dtos.Permission.LabInstitutionCreate
                        ) && (
                            <Link
                                className={"btn btn-primary"}
                                url={`/labs/${laboratoryIdNumber}/institutions/create`}
                            >
                                Create
                            </Link>
                        )}
                    </div>
                </div>
                {!showInstitutions ? (
                    <InlineLoading absoluteCenter={true} size={50} />
                ) : null}
                {showInstitutions ? (
                    <div className="card">
                        <div className="patient-tables">{createTable}</div>
                    </div>
                ) : null}
            </div>
        );
    }, [
        AuthenticationHelper,
        permissions,
        showInstitutions,
        createTable,
        showInActive,
        setShowInActive,
    ]);

    const renderDeleteConfirmation = useMemo(() => {
        const hasPermission = AuthenticationHelper.hasPermission(
            permissions,
            Dtos.Permission.LabInstitutionUpdate,
            undefined,
            undefined
        );

        if (hasPermission) {
            return (
                <Modal
                    open={openModal}
                    disableCloseOnOverlayClick={true}
                    onClose={() => {
                        setOpenModal(false);
                        setLabInstitution(undefined);
                    }}
                    size={ModalSize.Md}
                >
                    <div className="modal-header">
                        <div
                            className="modal-title"
                            style={{ minWidth: "540px" }}
                        >
                            <h2 className="mb-0">Delete</h2>
                        </div>
                        <button
                            type="button"
                            className="close"
                            aria-label="Close"
                            onClick={() => {
                                setOpenModal(false);
                                setLabInstitution(undefined);
                            }}
                        >
                            <FontAwesomeIcon
                                fixedWidth
                                icon={FontAwesomeIcons.Regular.TIMES}
                            />
                        </button>
                    </div>
                    <div className="modal-body">
                        <span>
                            Are you sure you want to delete the pathology
                            department "
                            <em>{labInstitution?.institution?.name}"</em> from
                            laboratory <strong>{laboratory?.name}</strong>.
                        </span>
                    </div>
                    <div className="modal-footer">
                        <button
                            type="button"
                            className="btn btn-primary"
                            onClick={() => {
                                deleteInstitutionLaboratory(labInstitution?.id);
                                setOpenModal(false);
                                setLabInstitution(undefined);
                            }}
                        >
                            Delete
                        </button>
                        <button
                            type="button"
                            className="btn btn-outline-primary"
                            onClick={() => {
                                setOpenModal(false);
                                setLabInstitution(undefined);
                            }}
                        >
                            Cancel
                        </button>
                    </div>
                </Modal>
            );
        }
        return null;
    }, [
        labInstitution,
        openModal,
        permissions,
        setOpenModal,
        labInstitution,
        setLabInstitution,
        laboratoryIdNumber,
        loadLaboratory,
    ]);

    return (
        <RestrictedLayout
            permission={Dtos.Permission.LabInstitutionView}
            loading={loadingLaboratory || loadingInstitutionLaboratories}
        >
            {renderContent}
            {renderDeleteConfirmation}
        </RestrictedLayout>
    );
};

const mapStateToProps = (
    state: ITmdState
): Partial<LabInstitutionsPageProps> => {
    let laboratory: Dtos.Laboratory | undefined = !(
        state.laboratories.formData instanceof Array
    )
        ? state.laboratories.formData
        : undefined;
    let institutionLaboratories: Dtos.InstitutionLaboratory[] | undefined = (
        state.institutionLaboratory.formData instanceof Array
    )
        ? state.institutionLaboratory.formData
        : undefined;
    let permissions = state.user.userSummary
        ? state.user.userSummary.permissions
        : [];

    return {
        laboratory: laboratory!,
        loadingLaboratory:
            state.laboratories.loadState &&
            state.laboratories.loadState.status == RequestState.Pending,
        loadLaboratorySuccess:
            state.laboratories.loadState &&
            state.laboratories.loadState.status == RequestState.Success,
        loadLaboratoryFailure:
            state.laboratories.loadState &&
            state.laboratories.loadState.status == RequestState.Failure,
        loadLaboratoryErrorCode: state.laboratories.loadState.errorCode,
        institutionLaboratories: institutionLaboratories!,
        loadingInstitutionLaboratories:
            state.institutionLaboratory.loadState &&
            state.institutionLaboratory.loadState.status == RequestState.Pending,
        loadInstitutionLaboratoriesSuccess:
            state.institutionLaboratory.loadState &&
            state.institutionLaboratory.loadState.status == RequestState.Success,
        loadInstitutionLaboratoriesFailure:
            state.institutionLaboratory.loadState &&
            state.institutionLaboratory.loadState.status == RequestState.Failure,
        loadInstitutionLaboratoriesErrorCode: state.institutionLaboratory.loadState.errorCode,
        permissions: permissions,
        pageSize: state.pagingPage.pageSize,
        deletingInstitutionLaboratory:
            state.institutionLaboratory.deleteState &&
            state.institutionLaboratory.deleteState.status ==
                RequestState.Pending,
        deleteInstitutionLaboratorySuccess:
            state.institutionLaboratory.deleteState &&
            state.institutionLaboratory.deleteState.status ==
                RequestState.Success,
    };
};

const mapDispatchToProps = (dispatch): Partial<LabInstitutionsPageProps> => {
    return {
        loadLaboratory: bindActionCreators(
            LaboratoryActions.LoadLaboratoryById,
            dispatch
        ),
        clearLaboratory: bindActionCreators(LaboratoryActions.Clear, dispatch),
        loadInstitutionLaboratories: bindActionCreators(InstitutionLaboratoryActions.LoadInstitutionLaboratoryByLaboratoryId, dispatch),
        clearInstitutionLaboratories: bindActionCreators(InstitutionLaboratoryActions.Clear, dispatch),
        setBreadcrumbs: bindActionCreators(
            NavigationActions.SetBreadcrumbs,
            dispatch
        ),
        setDrawerItems: bindActionCreators(
            NavigationActions.SetDrawerItems,
            dispatch
        ),
        setPageSize: bindActionCreators(
            PagingActions.PageSizeChanged,
            dispatch
        ),
        deleteInstitutionLaboratory: bindActionCreators(
            InstitutionLaboratoryActions.DeleteInstitutionLaboratory,
            dispatch
        )
    };
};

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(LabInstitutionsPage);
